import { Link } from 'react-router-dom'
import { useT } from '../i18n/context'
import { useAuthStore } from '../stores/auth'
import { BetaBadge } from './BetaBadge'

/**
 * Seiten-Footer (analog AppFooter des Nuxt-Frontends): Marke mit Beta-Badge
 * und Slogan, Produkt-Links (je nach Auth) und Rechts-Links, Jahr rechts.
 */
export function Footer() {
    const t = useT()
    const user = useAuthStore(s => s.user)
    const year = new Date().getFullYear()

    return (
        <footer className='border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900'>
            <div className='mx-auto flex max-w-6xl flex-col gap-8 px-4 py-8 sm:flex-row sm:gap-12'>
                <div className='max-w-xs'>
                    <div className='flex items-center gap-2'>
                        <span className='flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 dark:bg-indigo-500 text-white'>
                            <svg
                                className='h-4 w-4'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={2.4}
                                strokeLinecap='round'
                                aria-hidden='true'
                            >
                                <circle cx='12' cy='12' r='9' />
                                <path d='M12 7v5l3.5 2' />
                            </svg>
                        </span>
                        <span className='text-sm font-bold tracking-tight text-slate-800 dark:text-slate-100'>
                            Optimus Time
                        </span>
                        <BetaBadge />
                    </div>
                    <p className='mt-3 text-sm text-slate-600 dark:text-slate-300'>
                        {t('footer.tagline')}
                    </p>
                    <p className='mt-1 text-xs text-slate-500 dark:text-slate-400'>
                        {t('footer.betaNote')}
                    </p>
                </div>

                <nav
                    aria-label={t('footer.product')}
                    className='flex flex-col items-start gap-2.5'
                >
                    <h3 className='text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
                        {t('footer.product')}
                    </h3>
                    <FooterLink to='/'>{t('footer.home')}</FooterLink>
                    {user ? (
                        <FooterLink to='/billing/reports'>
                            {t('nav.reports')}
                        </FooterLink>
                    ) : (
                        <FooterLink to='/login'>{t('login.signin')}</FooterLink>
                    )}
                </nav>

                <nav
                    aria-label={t('footer.legalTitle')}
                    className='flex flex-col items-start gap-2.5'
                >
                    <h3 className='text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
                        {t('footer.legalTitle')}
                    </h3>
                    <FooterLink to='/impressum'>{t('footer.impressum')}</FooterLink>
                    <FooterLink to='/datenschutz'>
                        {t('footer.datenschutz')}
                    </FooterLink>
                    <FooterLink to='/agb'>{t('footer.agb')}</FooterLink>
                </nav>

                <p className='self-start text-xs text-slate-500 dark:text-slate-400 sm:ml-auto sm:self-center'>
                    © {year} Optimus Time
                </p>
            </div>
        </footer>
    )
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
    return (
        <Link
            to={to}
            className='text-sm text-slate-600 dark:text-slate-300 transition-colors hover:text-indigo-700 dark:hover:text-indigo-300'
        >
            {children}
        </Link>
    )
}
